import React, { useState, useMemo, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../../context/AuthContext';
import { updateTask } from '../../../../api/pmsApi';

// 아바타 색상 팔레트 (멤버별 고유 색상 자동 배정)
export const AVATAR_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#4CAF50', '#00BCD4',
  '#3F51B5', '#009688', '#FFC107', '#795548', '#607D8B'
];

export const getAvatarColorInfo = (identifier) => {
  if (!identifier) return { bg: '#adb5bd', text: 'light' };
  const str = String(identifier);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(Math.imul(hash, 2654435761));
  const index = hash % AVATAR_COLORS.length;
  const color = AVATAR_COLORS[index];
  const isDarkText = ['#FFC107', '#FF9800', '#00BCD4', '#4CAF50'].includes(color);
  return { bg: color, text: isDarkText ? 'dark' : 'light' };
};

function BoardTab({ toggleDrawer, tasks: propTasks = [], setTasks: propSetTasks, categories = [], isProjectMaster, canCreateTask, currentUser, project: propProject, setProject, onDataRefresh }) {
  // 실제 데이터를 내부 상태 없이 직접 사용 (props 기반)
  const tasks = propTasks;
  const setTasks = propSetTasks || (() => { });

  const { user: contextUser } = useContext(AuthContext);
  const user = currentUser || contextUser;


  // tasks에서 유니크한 멤버 목록 자동 추출 (중복 제거)
  const membersList = useMemo(() => {
    const memberMap = new Map();
    tasks.forEach(task => {
      (task.members || []).forEach(m => {
        const userId = m.user_id || m.user?.id;
        if (userId && !memberMap.has(userId)) {
          const userName = m.user?.name || '알수없음';
          const position = m.user?.org?.position || '';
          const colorInfo = getAvatarColorInfo(userId);
          memberMap.set(userId, {
            id: userId,
            name: position ? `${userName} ${position}` : userName,
            initials: userName.charAt(0),
            color: colorInfo.bg,
            text: colorInfo.text,
            photo: m.user?.org?.photo_path || m.user?.photo_path
          });
        }
      });
    });
    return Array.from(memberMap.values());
  }, [tasks]);

  const filterMembersList = useMemo(() => {
    const managerIds = new Set();
    tasks.forEach(task => {
      (task.members || []).forEach(m => {
        if (m.is_manager === 1 || m.is_manager === true) {
          const userId = m.user_id || m.user?.id;
          if (userId) managerIds.add(userId);
        }
      });
    });
    return membersList.filter(m => managerIds.has(m.id));
  }, [tasks, membersList]);

  const allMemberIds = useMemo(() => filterMembersList.map(m => m.id), [filterMembersList]);

  const [selectedMembers, setSelectedMembers] = useState([]);
  const [actualLaborModal, setActualLaborModal] = useState({ isOpen: false, userName: '' });
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  const openActualLaborModal = (userName) => {
    if (!userName) return;
    setActualLaborModal({ isOpen: true, userName });
    setIsAllExpanded(true);
  };
  const closeActualLaborModal = () => setActualLaborModal({ isOpen: false, userName: '' });

  const realUserTasks = useMemo(() => {
    if (!actualLaborModal.isOpen) return [];
    return (tasks || []).filter(task => {
      const taskMembers = task.members || [];
      return taskMembers.some(m => {
        const uName = m.user?.name || m.user_id;
        const uPos = m.user?.org?.position || '';
        const dName = uPos ? `${uName} ${uPos}` : uName;
        return dName === actualLaborModal.userName;
      });
    }).map(task => {
      const formatStr = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0].replace(/-/g, '.');
      };
      const sDate = formatStr(task.start_date);
      const eDate = formatStr(task.end_date);
      let days = 0;
      if (task.start_date && task.end_date) {
        const diff = Math.abs(new Date(task.end_date) - new Date(task.start_date));
        days = Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
      }
      const md = parseFloat(task.man_day) || parseFloat(task.mm) || days || 0;

      let prog = task.progress_rate || 0;
      const endDateIso = task.end_date ? task.end_date.split('T')[0] : '';
      const todayIso = new Date().toISOString().split('T')[0];

      let statusStr = `시작전(${prog}%)`;
      if (prog === 100) {
        statusStr = `완료(100%)`;
      } else if (endDateIso && endDateIso < todayIso) {
        statusStr = `지연(${prog}%)`;
      } else if (prog > 0) {
        statusStr = `진행중(${prog}%)`;
      }

      let folder = '지정되지 않은 분류';
      if (task.category_name) folder = task.category_name;
      else if (task.category && task.category.name) folder = task.category.name;
      else if (typeof task.category === 'string') folder = task.category;

      return {
        folder: folder,
        title: task.title || task.name || '제목 없음',
        start: sDate,
        end: eDate,
        status: statusStr,
        md: md
      };
    });
  }, [tasks, actualLaborModal]);

  const groupedRealData = useMemo(() => {
    return realUserTasks.reduce((acc, curr) => {
      if (!acc[curr.folder]) acc[curr.folder] = [];
      acc[curr.folder].push(curr);
      return acc;
    }, {});
  }, [realUserTasks]);

  const totalUserRealMD = useMemo(() => {
    return realUserTasks.reduce((sum, t) => sum + t.md, 0).toFixed(1);
  }, [realUserTasks]);
  const [collapsedCategories, setCollapsedCategories] = useState([]);
  const [viewMode, setViewMode] = useState('all');
  const [selectedCategoryId, setSelectedCategoryId] = useState(categories[0]?.id || '');
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);

  const hasInitializedMembers = React.useRef(false);

  // 멤버 목록이 로드되면 전체 선택 상태로 초기화 (최초 1회만)
  useEffect(() => {
    if (allMemberIds.length > 0 && !hasInitializedMembers.current) {
      setSelectedMembers(allMemberIds);
      hasInitializedMembers.current = true;
    }
  }, [allMemberIds]);

  // categories가 로드되면 첫번째 카테고리를 기본 선택
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: '', // 'completed_to_delay', 'overdue_stay', 'prevent_overdue'
    taskId: null,
    targetProgress: 0,
    targetEndDate: '',
    title: '',
    message: ''
  });

  // 통합 보기/개별 보기 전환 시 자동 펼치기
  useEffect(() => {
    if (viewMode === 'all') {
      setCollapsedCategories([]);
    } else if (viewMode === 'individual' && selectedCategoryId) {
      setCollapsedCategories(prev => prev.filter(id => String(id) !== String(selectedCategoryId)));
    }
  }, [viewMode, selectedCategoryId]);

  const expandAll = () => setCollapsedCategories([]);
  const collapseAll = () => setCollapsedCategories(categories.map(c => c.id));

  const todayStr = new Date().toISOString().split('T')[0];
  const todayDate = new Date();

  const toggleMemberFilter = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleCategory = (categoryId) => {
    setCollapsedCategories(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ taskId: task.id, categoryId: task.category_id }));
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragOverTarget(null);
  };

  const handleDragOver = (e, statusColumn, categoryId) => {
    e.preventDefault();
    if (statusColumn && categoryId) {
      setDragOverTarget(`${categoryId}-${statusColumn}`);
    }
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  // 날짜 필드를 'YYYY-MM-DD' 형식으로 정규화 (ISO 문자열 대응)
  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const getTaskStatus = (task) => {
    if (task.progress_rate === 100) return 'completed';
    const endDate = normalizeDate(task.end_date);
    if (endDate && endDate < todayStr) return 'overdue';
    if (task.progress_rate === 0) return 'pending';
    return 'in_progress';
  };

  const updateTaskProgress = (taskId, newProgress) => {
    setTasks(prevTasks => prevTasks.map(t => t.id === taskId ? { ...t, progress_rate: newProgress } : t));
  };

  const handleProgressUpdate = async (taskId, newProgress) => {
    const targetTask = tasks.find(t => t.id === taskId);
    const oldProgress = targetTask ? targetTask.progress_rate : newProgress;

    // 1. 하위 업무(Task) 프론트 즉각 렌더링
    updateTaskProgress(taskId, newProgress);

    // 2. 프로젝트(Project) 상태 즉각 렌더링 (Optimistic UI - 깜빡임 제거)
    if (setProject && propProject) {
      setProject(prev => {
        let total = 0;
        let count = 0;
        tasks.forEach(t => {
          count++;
          if (t.id === taskId) total += newProgress;
          else total += (t.progress_rate || 0);
        });
        const newAvg = count > 0 ? Math.round(total / count) : prev.progress_rate;
        
        let newProjStatus = prev.status;
        if (newAvg < 100 && prev.status === 'completed') {
          newProjStatus = 'in_progress';
        }
        return { ...prev, progress_rate: newAvg, status: newProjStatus };
      });
    }

    let newStatus = 'in_progress';
    if (newProgress === 100) newStatus = 'completed';
    else if (newProgress === 0) newStatus = 'pending';

    // 3. 백그라운드 서버 동기화
    try {
      const token = sessionStorage.getItem('token');
      await updateTask(taskId, { progress_rate: newProgress, status: newStatus }, token);
      
      // 서버에서 지시사항 상태 등 최종 데이터를 '조용히(로딩 스피너 없이)' 갱신
      if (onDataRefresh) {
        onDataRefresh(false);
      }
    } catch (err) {
      console.error('Update API error:', err);
      alert('상태 업데이트 중 오류가 발생했습니다.');
      // 롤백
      updateTaskProgress(taskId, oldProgress);
      if (onDataRefresh) onDataRefresh(false);
    }
  };

  const handleDrop = (e, statusColumn, targetCategoryId) => {
    e.preventDefault();
    setDragOverTarget(null);
    try {
      const dataStr = e.dataTransfer.getData('text/plain');
      if (!dataStr) return;

      const data = JSON.parse(dataStr);
      const taskId = data.taskId;
      const sourceCategoryId = data.categoryId;

      if (sourceCategoryId && sourceCategoryId !== targetCategoryId) {
        alert('동일한 분류 내에서만 이동이 가능합니다.');
        return;
      }

      const targetTask = tasks.find(t => t.id === taskId);
      if (!targetTask) return;

      const sourceColumnName = getTaskStatus(targetTask);

      // 1. 방어 및 수용: '일정 지연'으로 직접 드롭
      if (statusColumn === 'overdue') {
        const taskEndDate = normalizeDate(targetTask.end_date);
        const isPastDue = taskEndDate && taskEndDate < todayStr;
        
        if (sourceColumnName === 'completed' && isPastDue) {
          // 마감일이 지난 '완료' 카드를 '일정 지연'으로 드래그 시 정상 수용
          let newProgress = Math.max(10, Math.min(targetTask.progress_rate, 99));
          if (newProgress === 100) newProgress = 99; // 100% 미만으로 깎아야 지연 처리됨
          handleProgressUpdate(taskId, newProgress);
          return;
        } else {
          setConfirmModal({
            isOpen: true,
            type: 'prevent_overdue',
            taskId: null,
            targetProgress: 0,
            targetEndDate: '',
            title: '불가 안내',
            message: isPastDue 
              ? "'일정 지연' 상태로는 직접 이동할 수 없습니다.\n마감일이 경과하면 시스템에 의해 자동으로 분류됩니다.\n\n업무를 원래 위치로 되돌립니다."
              : "마감일이 지나지 않은 업무는 '일정 지연'으로 임의 이동할 수 없습니다.\n'진행 중' 또는 '시작 전'으로 이동해 주세요.\n\n업무를 원래 위치로 되돌립니다."
          });
          return;
        }
      }

      let newProgress = targetTask.progress_rate;
      if (statusColumn === 'pending') newProgress = 0;
      else if (statusColumn === 'in_progress') newProgress = Math.max(10, Math.min(targetTask.progress_rate, 99));
      else if (statusColumn === 'completed') newProgress = 100;

      // 2. 기한 경과 카드 이동 감지
      const taskEndDate = normalizeDate(targetTask.end_date);
      if ((statusColumn === 'pending' || statusColumn === 'in_progress') && taskEndDate && taskEndDate < todayStr) {
        // 완료(100%) -> 진행중/시작전
        if (sourceColumnName === 'completed') {
          setConfirmModal({
            isOpen: true,
            type: 'completed_to_delay',
            taskId,
            targetProgress: newProgress,
            targetEndDate: taskEndDate,
            title: '일정 지연 자동 분류 안내',
            message: `해당 업무는 마감일(${taskEndDate})이 이미 경과되었습니다.

완료(100%) 상태를 취소하실 경우, 시스템 규칙에 따라
즉시 '일정 지연' 상태로 자동 변경됩니다.

그래도 상태를 변경하시겠습니까?`
          });
          return;
        }

        // 일정 지연 -> 진행중/시작전
        if (sourceColumnName === 'overdue') {
          setConfirmModal({
            isOpen: true,
            type: 'prevent_overdue', // 취소 버튼 없이 '확인' 버튼만 띄우기 위해 타입 재사용
            taskId: null,
            targetProgress: 0,
            targetEndDate: '',
            title: '상태 변경 안내',
            message: `해당 업무는 마감일(${taskEndDate})이 이미 경과되었습니다.
기한이 지난 업무는 시스템 규칙에 의해 '일정 지연' 컬럼에 고정됩니다.

상태를 변경하려면 세부정보 창에서 마감일을 연장해 주세요.`
          });
          return;
        }
      }

      // 3. 정상 업데이트
      handleProgressUpdate(taskId, newProgress);

    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const getDDayString = (endDateStr) => {
    const end = new Date(endDateStr);
    const diffTime = end - todayDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'D-Day';
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
  };



  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const taskMembers = t.members || [];
      // 담당자 미배정 항목: 전체 선택 시에만 표시
      if (taskMembers.length === 0) return selectedMembers.length === allMemberIds.length;
      const taskMemberIds = taskMembers.map(m => m.user_id || m.user?.id);
      return taskMemberIds.some(id => selectedMembers.includes(id));
    });
  }, [tasks, selectedMembers, allMemberIds]);

  const renderCard = (task) => {
    const status = getTaskStatus(task);
    const isOverdue = status === 'overdue';
    const isCompleted = status === 'completed';

    // 우선순위 자동 계산 로직 (D-Day 기준)
    let priorityBadge = null;
    const end = new Date(normalizeDate(task.end_date));
    const diffDays = Math.ceil((end - todayDate) / (1000 * 60 * 60 * 24));

    if (!isCompleted) {
      if (diffDays <= 3) {
        priorityBadge = <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 fs-9" style={{ padding: '3px 6px' }}><i className="fas fa-arrow-up me-1"></i>긴급</span>;
      } else if (diffDays <= 7) {
        priorityBadge = <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 fs-9" style={{ padding: '3px 6px' }}><i className="fas fa-minus me-1"></i>보통</span>;
      } else {
        priorityBadge = <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 fs-9" style={{ padding: '3px 6px' }}><i className="fas fa-arrow-down me-1"></i>여유</span>;
      }
    }

    const maxAvatars = 3;
    const taskMembers = task.members || [];
    const extraAvatars = taskMembers.length - maxAvatars;

    // 연도 제외 기간 포맷 (예: 05.11)
    const formatStr = (str) => str.substring(5).replace(/-/g, '.');

    const assignees = taskMembers.filter(m => m.is_manager === true || m.is_manager === 1);
    const isAssignee = assignees.some(a =>
      String(a.user_id) === String(user?.id) ||
      String(a.user_id) === String(user?.userid) ||
      (a.user && String(a.user.userid) === String(user?.userid)) ||
      (a.user && String(a.user.id) === String(user?.id))
    );
    const isPureWatcher = !canCreateTask && !isAssignee;

    return (
      <div
        key={task.id}
        draggable={!isPureWatcher}
        onDragStart={(e) => {
          if (!isPureWatcher) handleDragStart(e, task);
        }}
        onDragEnd={handleDragEnd}
        className={`bg-white px-3 rounded shadow border position-relative ${isOverdue ? 'border-danger' : 'border-secondary border-opacity-50'}`}
        style={{ paddingTop: '10px', paddingBottom: '10px', transition: 'all 0.2s ease', userSelect: 'none', cursor: isPureWatcher ? 'default' : 'grab' }}
        onMouseEnter={(e) => {
          if (isPureWatcher) return;
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.classList.replace('shadow', 'shadow-lg');
        }}
        onMouseLeave={(e) => {
          if (isPureWatcher) return;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.classList.replace('shadow-lg', 'shadow');
        }}
      >
        {isOverdue && (
          <div className="position-absolute" style={{ top: '-8px', left: '-8px', zIndex: 10 }}>
            <span className="badge rounded-circle bg-danger text-white border border-white shadow-sm p-1" style={{ width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-exclamation" style={{ fontSize: '0.6rem' }}></i>
            </span>
          </div>
        )}


        <div
          style={{ cursor: isPureWatcher ? 'default' : 'pointer' }}
          onClick={() => {
            if (!isPureWatcher && toggleDrawer) toggleDrawer(task);
          }}
          title={isPureWatcher ? "권한 없음" : "클릭하여 세부정보 열기"}
        >
          <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
            <div className="fw-bolder fs-7 text-dark text-truncate" style={{ lineHeight: '1.4', paddingRight: isOverdue ? '20px' : '0' }} title={task.title}>
              {task.title}
            </div>
            {priorityBadge && (
              <div className="flex-shrink-0">
                {priorityBadge}
              </div>
            )}
          </div>

          <div className={`fs-8 fw-bold mb-2 ${isOverdue ? 'text-danger' : 'text-secondary'} d-flex flex-wrap align-items-center gap-1`} style={{ minWidth: 0 }}>
            <span style={{ whiteSpace: 'nowrap' }}><i className="far fa-calendar-alt me-1"></i>{formatStr(normalizeDate(task.start_date))} ~ {formatStr(normalizeDate(task.end_date))}</span>
            {!isCompleted && (
              <span className={`rounded border ${diffDays < 0 ? 'bg-danger bg-opacity-10 text-danger border-danger border-opacity-50' :
                diffDays <= 2 ? 'bg-danger text-white border-danger shadow-sm' :
                  diffDays <= 5 ? 'bg-warning text-dark border-warning shadow-sm' :
                    'bg-success bg-opacity-10 text-success border-success border-opacity-50'
                }`} style={{ fontSize: '0.65rem', padding: '1px 5px', whiteSpace: 'nowrap' }}>
                {getDDayString(normalizeDate(task.end_date))}
              </span>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-auto pt-2 border-top border-secondary border-opacity-10 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className={`badge border ${(normalizeDate(task.end_date) < todayStr && task.progress_rate < 100) ? 'bg-danger bg-opacity-10 text-danger border-danger' :
              task.progress_rate === 100 ? 'bg-success bg-opacity-10 text-success border-success' :
                task.progress_rate > 0 ? 'bg-primary bg-opacity-10 text-primary border-primary border-opacity-50' :
                  'bg-warning bg-opacity-10 text-warning border-warning border-opacity-50'
              }`}>
              {task.progress_rate}%
            </div>
            <div className="text-secondary opacity-75 fs-9 fw-bold d-flex gap-2">
              {/* 체크리스트 아이콘 */}
              {(task.checklist_count || task.instructions_count || 0) > 0 && (
                <span
                  style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.add('text-primary'); }}
                  onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.remove('text-primary'); }}
                  onClick={(e) => { e.stopPropagation(); if (!isPureWatcher && toggleDrawer) toggleDrawer(task, 'checklist'); }}
                  title={isPureWatcher ? "권한 없음" : "체크리스트 보기"}
                >
                  <i className="fas fa-tasks me-1"></i>{task.checklist_count || task.instructions_count || 0}
                </span>
              )}

              {/* 코멘트 아이콘 */}
              {(task.comments_count || 0) > 0 && (
                <span
                  style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.add('text-primary'); }}
                  onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.remove('text-primary'); }}
                  onClick={(e) => { e.stopPropagation(); if (!isPureWatcher && toggleDrawer) toggleDrawer(task, 'comments'); }}
                  title={isPureWatcher ? "권한 없음" : "코멘트 보기"}
                >
                  <i className="far fa-comment-dots me-1"></i>{task.comments_count}
                </span>
              )}

              {/* 첨부파일 아이콘 */}
              {(task.files_count || 0) > 0 && (
                <span
                  style={{ cursor: isPureWatcher ? 'default' : 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => { if (!isPureWatcher) e.currentTarget.classList.add('text-primary'); }}
                  onMouseLeave={(e) => { if (!isPureWatcher) e.currentTarget.classList.remove('text-primary'); }}
                  onClick={(e) => { e.stopPropagation(); if (!isPureWatcher && toggleDrawer) toggleDrawer(task, 'files'); }}
                  title={isPureWatcher ? "권한 없음" : "첨부파일 보기"}
                >
                  <i className="fas fa-paperclip me-1"></i>{task.files_count}
                </span>
              )}
            </div>
          </div>

          <div className="d-flex" style={{ paddingLeft: '5px' }}>
            {taskMembers.slice(0, maxAvatars).map((m, idx) => {
              const userId = m.user_id || m.user?.id;
              const userName = m.user?.name || '알수없음';
              const position = m.user?.org?.position || '';
              const isManager = m.is_manager === true || m.is_manager === 1;
              const displayName = position ? `${userName} ${position}` : userName;
              const tooltipName = isManager ? displayName : `[참조] ${displayName}`;
              
              const memberInfo = membersList.find(ml => ml.id === userId);
              const colorInfo = getAvatarColorInfo(userId);
              const bgColor = memberInfo?.color || colorInfo.bg;
              const textColor = memberInfo?.text || colorInfo.text;
              const photoPath = memberInfo?.photo;

              const avatarSize = isManager ? '28px' : '26px';
              const opacity = isManager ? 1 : 0.65;
              const borderStyle = isManager ? 'solid' : 'dashed';
              const borderColor = isManager ? '#fff' : '#6c757d';

              return (
                <div key={userId || idx} className="avatar-wrapper" style={{ marginLeft: idx > 0 ? '-5px' : '0', zIndex: 10 - idx, opacity }}>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm overflow-hidden"
                    onClick={(e) => {
                      e.stopPropagation();
                      openActualLaborModal(tooltipName);
                    }}
                    style={{
                      width: avatarSize, height: avatarSize, backgroundColor: photoPath ? 'transparent' : bgColor, fontSize: '0.7rem',
                      color: textColor === 'dark' ? '#333' : '#fff', cursor: 'zoom-in',
                      border: `1px ${borderStyle} ${borderColor}`
                    }}
                    title="상세내역 보기"
                  >
                    {photoPath ? <img src={photoPath} alt={userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : userName.charAt(0)}
                  </div>
                  <div className="avatar-tooltip">
                    {tooltipName}
                  </div>
                </div>
              );
            })}
            {/* 완료된 업무는 +N명 생략, 진행중인 업무만 표시 */}
            {extraAvatars > 0 && !isCompleted && (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-light text-secondary fw-bolder shadow-sm border border-white"
                style={{
                  width: '28px', height: '28px', fontSize: '0.65rem',
                  marginLeft: '-5px', zIndex: 0
                }}
                title={`${extraAvatars}명 더 있음`}
              >
                +{extraAvatars}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyColumn = (type) => {
    let iconClass = '';
    if (type === 'pending') iconClass = 'fas fa-mug-hot';
    else if (type === 'in_progress') iconClass = 'fas fa-rocket';
    else if (type === 'overdue') iconClass = 'fas fa-exclamation-triangle';
    else if (type === 'completed') iconClass = 'fas fa-box-open';

    return (
      <div className="position-relative h-100 w-100 flex-grow-1 d-flex align-items-center justify-content-center text-secondary fs-8 fw-bold bg-white rounded overflow-hidden" style={{ minHeight: '120px' }}>
        <i className={`${iconClass} position-absolute`} style={{ fontSize: '5rem', color: '#adb5bd', opacity: '0.1', zIndex: 0 }}></i>
        <span className="position-relative" style={{ zIndex: 1 }}>해당되는 업무가 없습니다.</span>
      </div>
    );
  };

  return (
    <div className="d-flex flex-column h-100 animation-fade-in gap-3">
      <style>{`
        .btn-custom-toggle { color: #495057; border: 1px solid #ced4da; background-color: #fff; }
        .btn-custom-toggle:hover { color: #4c6ef5 !important; border-color: #4c6ef5 !important; background-color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle { background-color: #4c6ef5 !important; border-color: #4c6ef5 !important; color: #fff !important; }
        .btn-check:checked + .btn-custom-toggle:hover { background-color: #3b5bdb !important; border-color: #3b5bdb !important; color: #fff !important; }
        .avatar-wrapper { position: relative; display: inline-block; }
        .avatar-wrapper:hover .avatar-tooltip { visibility: visible; opacity: 1; transform: translate(-50%, -4px); }
        .avatar-tooltip {
          visibility: hidden; opacity: 0; position: absolute; bottom: 100%; left: 50%; transform: translate(-50%, 0);
          background-color: #212529; color: #fff; text-align: center; border-radius: 4px; padding: 4px 8px;
          font-size: 0.7rem; font-weight: bold; white-space: nowrap; transition: all 0.2s ease; z-index: 100;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1); pointer-events: none; margin-bottom: 2px;
        }
        .avatar-tooltip::after {
          content: ""; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          border-width: 4px; border-style: solid; border-color: #212529 transparent transparent transparent;
        }
      `}</style>
      {/* 1줄 고정, 가로 스크롤 (Horizontal Scroll) 필터 영역 */}
      <div className="bg-white rounded shadow-sm border border-secondary border-opacity-25 p-3 d-flex flex-column gap-2 flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <h6 className="fw-bolder text-dark m-0 d-flex align-items-center" style={{ fontSize: '16px' }}>
              <i className="fas fa-filter text-primary me-2"></i>담당자 필터
            </h6>
            <button
              className="btn btn-sm btn-light border p-0 d-flex align-items-center justify-content-center shadow-none"
              style={{ width: '24px', height: '24px', borderRadius: '4px' }}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              title={isFilterExpanded ? "필터 접기" : "필터 펼치기"}
            >
              <i className={`fas fa-chevron-${isFilterExpanded ? 'up' : 'down'} text-secondary`} style={{ fontSize: '0.75rem' }}></i>
            </button>
          </div>
          <span className="text-secondary fs-8 fw-bold">전체상세업무: {filteredTasks.length}개</span>
        </div>

        {isFilterExpanded && (
          <div
            className="d-flex flex-wrap gap-2 pt-2 border-top border-secondary border-opacity-10 overflow-y-auto custom-scrollbar pb-2"
            style={{ maxHeight: '86px' }}
          >
            <div
              onClick={() => setSelectedMembers(prev => prev.length === allMemberIds.length ? [] : allMemberIds)}
              className={`d-flex align-items-center px-3 py-1 rounded-pill transition-all flex-shrink-0 ${selectedMembers.length === allMemberIds.length ? 'shadow-sm' : 'bg-secondary bg-opacity-10 opacity-75'}`}
              style={{
                userSelect: 'none', cursor: 'pointer',
                border: selectedMembers.length === allMemberIds.length ? '1px solid #d35400' : '1px solid rgba(108, 117, 125, 0.5)',
                backgroundColor: selectedMembers.length === allMemberIds.length ? 'rgba(211, 84, 0, 0.1)' : ''
              }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
                style={{ width: '20px', height: '20px', fontSize: '0.65rem', backgroundColor: selectedMembers.length === allMemberIds.length ? '#d35400' : '#6c757d', color: '#fff' }}
              >
                <i className="fas fa-check"></i>
              </div>
              <span
                className={`ms-2 fs-8 fw-bold ${selectedMembers.length === allMemberIds.length ? '' : 'text-secondary'}`}
                style={{ color: selectedMembers.length === allMemberIds.length ? '#d35400' : '' }}
              >
                전체
              </span>
            </div>

            {[...filterMembersList].sort((a, b) => a.name.localeCompare(b.name)).map(member => {
              const isSelected = selectedMembers.includes(member.id);
              return (
                <div
                  key={member.id}
                  onClick={() => toggleMemberFilter(member.id)}
                  onDoubleClick={() => setSelectedMembers([member.id])}
                  className={`d-flex align-items-center px-3 py-1 rounded-pill border transition-all flex-shrink-0 ${isSelected ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'border-secondary border-opacity-25 bg-light opacity-75'}`}
                  style={{ userSelect: 'none', cursor: 'pointer' }}
                  title="필터 켜기/끄기 (더블클릭: 단독 필터)"
                >
                  <div
                    onClick={(e) => { e.stopPropagation(); openActualLaborModal(member.name); }}
                    style={{ cursor: 'zoom-in', display: 'flex', alignItems: 'center' }}
                    title="상세내역 보기"
                  >
                    {member.photo ? (
                      <img src={member.photo} alt={member.name} className="rounded-circle shadow-sm" style={{ width: '20px', height: '20px', objectFit: 'cover', opacity: isSelected ? 1 : 0.6 }} />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
                        style={{ width: '20px', height: '20px', fontSize: '0.65rem', backgroundColor: isSelected ? member.color : '#adb5bd', color: isSelected && member.text === 'dark' ? '#333' : '#fff' }}
                      >
                        {member.initials}
                      </div>
                    )}
                  </div>
                  <span className={`ms-2 fs-8 fw-bold ${isSelected ? 'text-primary' : 'text-secondary'}`}>{member.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="d-flex flex-column flex-grow-1 overflow-hidden">
        {/* 보기 모드 (통합/개별) 및 펼치기/접기 컨트롤 */}
        <div className="d-flex justify-content-between align-items-center mb-0 px-1 flex-shrink-0">
          <div className="btn-group" role="group">
            <input type="radio" className="btn-check" id="boardViewModeAll" checked={viewMode === 'all'} onChange={() => setViewMode('all')} />
            <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="boardViewModeAll">
              <i className="fas fa-list-ul me-1"></i> 통합 보기
            </label>

            <input type="radio" className="btn-check" id="boardViewModeInd" checked={viewMode === 'individual'} onChange={() => setViewMode('individual')} />
            <label className="btn btn-custom-toggle px-3 fw-bold" htmlFor="boardViewModeInd">
              <i className="fas fa-file-alt me-1"></i> 개별 보기
            </label>
          </div>

          <div className="d-flex align-items-center">
            {viewMode === 'all' ? (
              <div className="d-flex gap-2">
                <button className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center shadow-sm" onClick={expandAll} style={{ padding: '4px 10px' }}>
                  <i className="fas fa-angle-double-down me-2"></i> 전체 펼치기
                </button>
                <button className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center shadow-sm" onClick={collapseAll} style={{ padding: '4px 10px' }}>
                  <i className="fas fa-angle-double-up me-2"></i> 전체 접기
                </button>
              </div>
            ) : (
              <div className="d-flex align-items-center">
                <span className="fw-bold text-secondary me-2">분류 :</span>
                <select
                  className="form-select shadow-sm fw-bold text-dark text-truncate"
                  style={{ width: '250px', borderColor: '#ced4da' }}
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto custom-scrollbar pe-2">
          {(viewMode === 'all' ? categories : categories.filter(c => String(c.id) === String(selectedCategoryId))).map(category => {
            const catTasks = filteredTasks.filter(t => t.category_id === category.id);
            const isEmptyCategory = catTasks.length === 0;
            const isCollapsed = collapsedCategories.includes(category.id) || isEmptyCategory;

            const catPending = catTasks.filter(t => getTaskStatus(t) === 'pending');
            const catInProgress = catTasks.filter(t => getTaskStatus(t) === 'in_progress');
            const catCompleted = catTasks.filter(t => getTaskStatus(t) === 'completed');
            const catOverdue = catTasks.filter(t => getTaskStatus(t) === 'overdue');

            return (
              <div key={category.id} className="mb-4 bg-white rounded shadow-sm border border-secondary border-opacity-25 overflow-hidden">
                <div
                  className={`px-4 bg-light border-bottom border-secondary border-opacity-25 d-flex justify-content-between align-items-center transition-all ${isEmptyCategory ? '' : 'cursor-pointer'}`}
                  style={{ paddingTop: '12px', paddingBottom: '12px', opacity: isEmptyCategory ? 0.6 : 1 }}
                  onClick={() => { if (!isEmptyCategory) toggleCategory(category.id); }}
                  onMouseEnter={(e) => { if (!isEmptyCategory) e.currentTarget.style.backgroundColor = '#f1f3f5'; }}
                  onMouseLeave={(e) => { if (!isEmptyCategory) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                >
                  <h5 className="fw-bolder text-dark m-0 d-flex align-items-center">
                    {isEmptyCategory ? (
                      <i className="fas fa-minus text-secondary me-3 opacity-50" style={{ width: '15px' }}></i>
                    ) : (
                      <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'} text-secondary me-3`} style={{ width: '15px' }}></i>
                    )}
                    {category.name}
                    <span className="ms-3 badge bg-secondary bg-opacity-10 text-secondary fs-8 border border-secondary border-opacity-25 rounded-pill">{catTasks.length}</span>
                  </h5>
                </div>

                {!isCollapsed && !isEmptyCategory && (
                  <div className="d-flex p-3 gap-3 overflow-x-hidden" style={{ backgroundColor: '#fdfdfe' }}>

                    {/* 시작 전 */}
                    <div className="d-flex flex-column" style={{ flex: '1 1 0%', minWidth: '160px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                        <span className="fw-bolder text-warning fs-7" style={{ color: '#d39e00' }}><i className="fas fa-circle opacity-75 me-2" style={{ fontSize: '0.6rem', color: '#ffc107' }}></i>시작 전</span>
                        <span className="badge bg-warning bg-opacity-25 text-dark rounded-pill border border-warning border-opacity-25">{catPending.length}</span>
                      </div>
                      <div className={`flex-grow-1 rounded d-flex flex-column gap-2 ${dragOverTarget === category.id + '-pending' ? 'border border-2 border-warning shadow-sm' : ''}`} style={{ backgroundColor: dragOverTarget === category.id + '-pending' ? '#e2e8f0' : '#fffcf2', minHeight: '150px', padding: '6px', borderStyle: dragOverTarget === category.id + '-pending' ? 'dashed' : 'none', transition: 'all 0.2s ease' }} onDragOver={(e) => handleDragOver(e, 'pending', category.id)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'pending', category.id)}>
                        {catPending.length > 0 ? catPending.map(renderCard) : renderEmptyColumn('pending')}
                      </div>
                    </div>

                    {/* 진행 중 */}
                    <div className="d-flex flex-column" style={{ flex: '1 1 0%', minWidth: '160px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                        <span className="fw-bolder text-primary fs-7"><i className="fas fa-spinner fa-spin text-primary opacity-75 me-2"></i>진행 중</span>
                        <span className="badge bg-primary bg-opacity-25 text-primary rounded-pill">{catInProgress.length}</span>
                      </div>
                      <div className={`flex-grow-1 rounded d-flex flex-column gap-2 ${dragOverTarget === category.id + '-in_progress' ? 'border border-2 border-primary shadow-sm' : ''}`} style={{ backgroundColor: dragOverTarget === category.id + '-in_progress' ? '#e2e8f0' : '#f0f5ff', minHeight: '150px', padding: '6px', borderStyle: dragOverTarget === category.id + '-in_progress' ? 'dashed' : 'none', transition: 'all 0.2s ease' }} onDragOver={(e) => handleDragOver(e, 'in_progress', category.id)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'in_progress', category.id)}>
                        {catInProgress.length > 0 ? catInProgress.map(renderCard) : renderEmptyColumn('in_progress')}
                      </div>
                    </div>

                    {/* 일정 지연 */}
                    <div className="d-flex flex-column" style={{ flex: '1 1 0%', minWidth: '160px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                        <span className="fw-bolder text-danger fs-7"><i className="fas fa-exclamation-triangle text-danger opacity-75 me-2"></i>일정 지연</span>
                        <span className="badge bg-danger bg-opacity-25 text-danger rounded-pill">{catOverdue.length}</span>
                      </div>
                      <div className={`flex-grow-1 rounded d-flex flex-column gap-2 ${dragOverTarget === category.id + '-overdue' ? 'border border-2 border-danger shadow-sm' : ''}`} style={{ backgroundColor: dragOverTarget === category.id + '-overdue' ? '#fde8e8' : '#fff5f5', minHeight: '150px', padding: '6px', borderStyle: dragOverTarget === category.id + '-overdue' ? 'dashed' : 'none', transition: 'all 0.2s ease' }} onDragOver={(e) => handleDragOver(e, 'overdue', category.id)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'overdue', category.id)}>
                        {catOverdue.length > 0 ? catOverdue.map(renderCard) : renderEmptyColumn('overdue')}
                      </div>
                    </div>

                    {/* 완료 */}
                    <div className="d-flex flex-column" style={{ flex: '1 1 0%', minWidth: '160px' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                        <span className="fw-bolder text-success fs-7"><i className="fas fa-check-circle text-success opacity-75 me-2"></i>완료</span>
                        <span className="badge bg-success bg-opacity-25 text-success rounded-pill">{catCompleted.length}</span>
                      </div>
                      <div className={`flex-grow-1 rounded d-flex flex-column gap-2 ${dragOverTarget === category.id + '-completed' ? 'border border-2 border-success shadow-sm' : ''}`} style={{ backgroundColor: dragOverTarget === category.id + '-completed' ? '#d1e7dd' : '#f6ffed', minHeight: '150px', padding: '6px', borderStyle: dragOverTarget === category.id + '-completed' ? 'dashed' : 'none', transition: 'all 0.2s ease' }} onDragOver={(e) => handleDragOver(e, 'completed', category.id)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'completed', category.id)}>
                        {catCompleted.length > 0 ? catCompleted.map(renderCard) : renderEmptyColumn('completed')}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 커스텀 상태 변경 확인 모달 (Portal을 사용해 body 최상단에 렌더링) */}
      {confirmModal.isOpen && createPortal(
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999 }}>
          <div className="bg-white rounded-3 shadow-lg overflow-hidden" style={{ width: '450px', maxWidth: '90%' }}>
            {/* 타이틀 영역 (회색 배경) */}
            <div className="bg-light border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bolder text-dark mb-0 d-flex align-items-center">
                {confirmModal.type === 'prevent_overdue' ? (
                  <i className="fas fa-ban text-danger me-2"></i>
                ) : (
                  <i className="fas fa-exclamation-circle text-warning me-2"></i>
                )}
                {confirmModal.title}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
              ></button>
            </div>

            {/* 본문 영역 (흰색 배경) - 폰트 크기 0.85rem으로 축소 */}
            <div className="px-4 py-4 text-dark" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '0.85rem', wordBreak: 'keep-all' }}>
              {confirmModal.message}
            </div>

            {/* 버튼 영역 (회색 배경) */}
            <div className="bg-light border-top px-4 py-2 d-flex justify-content-end gap-2">
              {confirmModal.type === 'prevent_overdue' ? (
                <button
                  className="btn btn-primary px-4"
                  onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                >
                  확인
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-light border px-3"
                    onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                  >
                    취소
                  </button>
                  <button
                    className={confirmModal.type === 'completed_to_delay' ? 'btn btn-danger px-3' : 'btn btn-primary px-3'}
                    onClick={() => {
                      handleProgressUpdate(confirmModal.taskId, confirmModal.targetProgress);
                      setConfirmModal({ ...confirmModal, isOpen: false });
                    }}
                  >
                    {confirmModal.type === 'completed_to_delay' ? '이동' : '업데이트'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 실투입 상세내역 모달 */}
      {actualLaborModal.isOpen && createPortal(
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99999 }} onClick={closeActualLaborModal}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-bottom bg-light py-3 px-4 rounded-top-4">
                <h5 className="modal-title fw-bolder fs-5" style={{ color: '#2d3748' }}>
                  <i className="fas fa-user-circle me-2 text-primary"></i>[{actualLaborModal.userName}] 실투입 상세 내역
                </h5>
                <button type="button" className="btn-close" onClick={closeActualLaborModal}></button>
              </div>
              <div className="modal-body py-3 px-4 bg-white">

                <div className="mb-3 d-flex justify-content-between align-items-end pt-3 pb-2 px-4 rounded-4 text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center mb-1">
                    <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '42px', height: '42px' }}>
                      <i className="fas fa-chart-line fs-4 text-white"></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0 text-white text-opacity-75 fs-8">전체 누적 투입일</h6>
                      <h3 className="fw-bolder mb-0 text-white mt-1">{totalUserRealMD} M/D</h3>
                    </div>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-light text-primary rounded-pill fw-bolder px-3 py-1 shadow-sm d-flex align-items-center"
                      onClick={() => setIsAllExpanded(!isAllExpanded)}
                      style={{ fontSize: '0.8rem' }}
                    >
                      {isAllExpanded ? <><i className="fas fa-chevron-up me-1"></i>전체 접기</> : <><i className="fas fa-chevron-down me-1"></i>전체 펼치기</>}
                    </button>
                  </div>
                </div>

                {Object.keys(groupedRealData).length > 0 ? (
                  <div className="accordion" id="boardRealDataAccordion">
                    {Object.entries(groupedRealData).map(([folder, folderTasks], idx) => {
                      const folderTotal = folderTasks.reduce((sum, t) => sum + t.md, 0);
                      return (
                        <div className="accordion-item border border-light mb-2 shadow-sm rounded-3 overflow-hidden" key={idx}>
                          <h2 className="accordion-header bg-white">
                            <button className={`accordion-button bg-light bg-opacity-50 shadow-none text-dark border-bottom py-2 px-4 ${isAllExpanded ? '' : 'collapsed'}`} type="button" data-bs-toggle="collapse" data-bs-target={`#boardCollapse${idx}`} aria-expanded={isAllExpanded}>
                              <div className="d-flex align-items-center w-100">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                                  <i className="far fa-folder-open fs-6"></i>
                                </div>
                                <span className="fs-7 fw-bolder text-dark">{folder}</span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary ms-2 rounded-pill px-2 py-1 fs-8">{folderTasks.length}건</span>

                                <div className="ms-auto fw-bolder fs-7 pe-1" style={{ color: '#764ba2', width: '100px', textAlign: 'right' }}>
                                  총 {folderTotal.toFixed(1)} M/D
                                </div>
                              </div>
                            </button>
                          </h2>
                          <div id={`boardCollapse${idx}`} className={`accordion-collapse collapse ${isAllExpanded ? 'show' : ''}`}>
                            <div className="accordion-body p-0 bg-white">
                              <ul className="list-group list-group-flush">
                                {folderTasks.map((t, i) => (
                                  <li className="list-group-item d-flex justify-content-between align-items-center py-2 px-4 bg-transparent border-light border-bottom" key={i}>
                                    <div className="d-flex align-items-start ms-4">
                                      <span className="text-secondary me-2 fw-bold" style={{ opacity: 0.4 }}>└</span>
                                      <div className="d-flex flex-column">
                                        <span className="fw-bold text-secondary fs-7">{t.title}</span>
                                        <span className="text-muted fw-semibold mt-1" style={{ fontSize: '0.7rem' }}><i className="far fa-calendar-alt me-1 opacity-75"></i>{t.start} ~ {t.end}</span>
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 pe-1">
                                      <span className={`badge ${t.status.includes('완료') ? 'bg-success' : t.status.includes('지연') ? 'bg-danger' : t.status.includes('진행중') ? 'bg-primary' : 'bg-secondary'} bg-opacity-10 ${t.status.includes('완료') ? 'text-success' : t.status.includes('지연') ? 'text-danger' : t.status.includes('진행중') ? 'text-primary' : 'text-secondary'} px-2 py-1 rounded-pill fw-bold fs-8`}>{t.status}</span>
                                      <span className="fw-bolder fs-7" style={{ color: '#4a5568', width: '100px', textAlign: 'right' }}>{t.md.toFixed(1)} M/D</span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted bg-light rounded-3">
                    <i className="fas fa-info-circle fs-2 mb-2 text-secondary opacity-50"></i>
                    <h6 className="fw-bold text-dark">데이터가 없습니다</h6>
                    <p className="mb-0 fs-8">해당 사용자로 배정된 업무 내역이 존재하지 않습니다.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top bg-light rounded-bottom-4 py-2 px-4">
                <button type="button" className="btn btn-secondary btn-sm px-4 fw-bold rounded-pill shadow-sm" onClick={closeActualLaborModal}>닫기</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

export default BoardTab;
